// TripPlan Data Model
export const tripPlanModel = {
  // TripPlan Collection Structure
  collection: 'tripPlans',
  
  // Default TripPlan Document Structure
  defaultDocument: {
    id: '', // Auto-generated by Firestore
    userId: '', // Reference to user who created the trip
    userName: '', // User's display name
    userRole: 'customer', // customer, momo, girl (excluding admin and self)
    
    // Basic Trip Information
    tripName: '', // ชื่อทริป
    tripDate: '', // วันที่ (ISO string)
    tripLocation: '', // สถานที่ที่พัก
    tripTheme: '', // theme ของทริป
    
    // Trip Details
    participantCount: 1, // จำนวนผู้เข้าร่วม
    description: '', // รายละเอียดเบื้องต้น
    requiredGirls: 1, // จำนวนน้องที่ต้องการให้ดูแลในงาน
    
    // Additional Information
    budget: 0, // งบประมาณ (optional)
    duration: 1, // ระยะเวลา (วัน)
    specialRequirements: '', // ความต้องการพิเศษ
    
    // Status and Metadata
    status: 'draft', // draft, active, completed, cancelled
    isPublic: false, // สามารถให้คนอื่นเห็นได้หรือไม่
    createdAt: '', // ISO string
    updatedAt: '', // ISO string
    
    // References
    bookings: [], // Array of booking IDs that reference this trip
    tags: [], // Array of tags for categorization
    
    // Checklist for 3-Step Customer Journey
    checklist: {
      // Step 1: Plan Trip
      planTrip: {
        tripCreated: false,
        budgetSet: false,
        locationConfirmed: false,
        dateConfirmed: false,
        themeSelected: false
      },
      // Step 2: Prepare Trip
      prepareTrip: {
        accommodationBooked: false,
        restaurantBooked: false,
        transportationArranged: false,
        girlsBooked: false,
        suppliesPrepared: false
      },
      // Step 3: Activity Trip
      activityTrip: {
        tripConfirmed: false,
        girlsCheckedIn: false,
        activitiesCompleted: false,
        paymentCompleted: false,
        reviewSubmitted: false
      }
    },
    
    // Check-in Configuration
    checkinConfig: {
      // Enable check-in for different groups
      enabled: {
        organizers: false, // ผู้จัด/ผู้ช่วยจัด
        participants: false, // ผู้เข้าร่วมกิจกรรม
        girls: false // น้องๆ
      },
      // Check-in settings
      settings: {
        allowManualCheckin: true, // อนุญาตให้ check-in แบบ manual
        allowQRCheckin: true, // อนุญาตให้ check-in แบบ QR code
        requireLocation: false, // ต้องการตำแหน่งที่ตั้ง (สำหรับอนาคต)
        checkinRadius: 100 // รัศมี check-in เป็นเมตร (สำหรับอนาคต)
      },
      // Check-in status tracking
      status: {
        organizersCheckedIn: [], // Array of user IDs who checked in
        participantsCheckedIn: [], // Array of user IDs who checked in
        girlsCheckedIn: [], // Array of girl IDs who checked in
        lastCheckinTime: null, // Timestamp of last check-in
        totalCheckins: 0 // Total number of check-ins
      }
    },
    
    // Progress tracking
    currentStep: 1, // 1, 2, 3, or 4 (completed)
    lastUpdated: '', // ISO string
  },
  
  // TripPlan Status Options
  statusOptions: [
    { value: 'draft', label: 'ร่าง', color: 'bg-gray-100 text-gray-800' },
    { value: 'active', label: 'ใช้งาน', color: 'bg-green-100 text-green-800' },
    { value: 'completed', label: 'เสร็จสิ้น', color: 'bg-blue-100 text-blue-800' },
    { value: 'cancelled', label: 'ยกเลิก', color: 'bg-red-100 text-red-800' }
  ],
  
  // TripPlan Theme Options
  themeOptions: [
    { value: 'birthday', label: 'วันเกิด', icon: '🎂' },
    { value: 'anniversary', label: 'วันครบรอบ', icon: '💕' },
    { value: 'business', label: 'ธุรกิจ', icon: '💼' },
    { value: 'party', label: 'ปาร์ตี้', icon: '🎉' },
    { value: 'romantic', label: 'โรแมนติก', icon: '🌹' },
    { value: 'family', label: 'ครอบครัว', icon: '👨‍👩‍👧‍👦' },
    { value: 'friends', label: 'เพื่อน', icon: '👥' },
    { value: 'celebration', label: 'เฉลิมฉลอง', icon: '🥳' },
    { value: 'other', label: 'อื่นๆ', icon: '🎭' }
  ],
  
  // Validation Rules
  validation: {
    tripName: {
      required: true,
      minLength: 3,
      maxLength: 100,
      message: 'ชื่อทริปต้องมี 3-100 ตัวอักษร'
    },
    tripDate: {
      required: true,
      message: 'กรุณาเลือกวันที่ทริป'
    },
    tripLocation: {
      required: false,
      minLength: 3,
      maxLength: 200,
      message: 'สถานที่ต้องมี 3-200 ตัวอักษร'
    },
    tripTheme: {
      required: true,
      message: 'กรุณาเลือก theme ของทริป'
    },
    participantCount: {
      required: true,
      min: 1,
      max: 100,
      message: 'จำนวนผู้เข้าร่วมต้องอยู่ระหว่าง 1-100 คน'
    },
    requiredGirls: {
      required: true,
      min: 1,
      max: 50,
      message: 'จำนวนน้องที่ต้องการต้องอยู่ระหว่าง 1-50 คน'
    },
    description: {
      required: false,
      maxLength: 1000,
      message: 'รายละเอียดต้องไม่เกิน 1000 ตัวอักษร'
    }
  }
};

// Helper Functions
export const createTripPlan = (userId, userName, userRole, data = {}) => {
  const now = new Date().toISOString();
  
  return {
    ...tripPlanModel.defaultDocument,
    userId,
    userName,
    userRole,
    ...data,
    createdAt: now,
    updatedAt: now,
    lastUpdated: now,
    // Initialize checklist with default values
    checklist: {
      planTrip: {
        tripCreated: true, // Auto-complete when trip is created
        budgetSet: data.budget > 0,
        locationConfirmed: !!data.tripLocation,
        dateConfirmed: !!data.tripDate,
        themeSelected: !!data.tripTheme
      },
      prepareTrip: {
        accommodationBooked: false,
        restaurantBooked: false,
        transportationArranged: false,
        girlsBooked: false,
        suppliesPrepared: false
      },
      activityTrip: {
        tripConfirmed: false,
        girlsCheckedIn: false,
        activitiesCompleted: false,
        paymentCompleted: false,
        reviewSubmitted: false
      }
    },
    // Initialize check-in configuration with default values
    checkinConfig: {
      enabled: {
        organizers: false,
        participants: false,
        girls: false
      },
      settings: {
        allowManualCheckin: true,
        allowQRCheckin: true,
        requireLocation: false,
        checkinRadius: 100
      },
      status: {
        organizersCheckedIn: [],
        participantsCheckedIn: [],
        girlsCheckedIn: [],
        lastCheckinTime: null,
        totalCheckins: 0
      }
    },
    currentStep: 1
  };
};

export const validateTripPlan = (tripPlan) => {
  const errors = {};
  const rules = tripPlanModel.validation;
  
  // Validate trip name
  if (!tripPlan.tripName || tripPlan.tripName.length < rules.tripName.minLength) {
    errors.tripName = rules.tripName.message;
  }
  
  // Validate trip date
  if (!tripPlan.tripDate) {
    errors.tripDate = rules.tripDate.message;
  }
  
  // Validate trip location (only if provided)
  if (tripPlan.tripLocation && tripPlan.tripLocation.length < rules.tripLocation.minLength) {
    errors.tripLocation = rules.tripLocation.message;
  }
  
  // Validate trip theme
  if (!tripPlan.tripTheme) {
    errors.tripTheme = rules.tripTheme.message;
  }
  
  // Validate participant count
  if (!tripPlan.participantCount || tripPlan.participantCount < rules.participantCount.min) {
    errors.participantCount = rules.participantCount.message;
  }
  
  // Validate required girls
  if (!tripPlan.requiredGirls || tripPlan.requiredGirls < rules.requiredGirls.min) {
    errors.requiredGirls = rules.requiredGirls.message;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const formatTripPlanForDisplay = (tripPlan) => {
  const theme = tripPlanModel.themeOptions.find(t => t.value === tripPlan.tripTheme);
  const status = tripPlanModel.statusOptions.find(s => s.value === tripPlan.status);
  
  return {
    ...tripPlan,
    themeDisplay: theme ? `${theme.icon} ${theme.label}` : '🎭 อื่นๆ',
    statusDisplay: status ? status.label : 'ร่าง',
    statusColor: status ? status.color : 'bg-gray-100 text-gray-800',
    formattedDate: new Date(tripPlan.tripDate).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }),
    checklistProgress: calculateChecklistProgress(tripPlan.checklist)
  };
};

// Helper function to calculate checklist progress
export const calculateChecklistProgress = (checklist) => {
  if (!checklist) return 0;
  
  const allSteps = Object.values(checklist);
  const totalItems = allSteps.reduce((acc, step) => acc + Object.keys(step).length, 0);
  const completedItems = allSteps.reduce((acc, step) => 
    acc + Object.values(step).filter(item => item).length, 0
  );
  
  console.log('🔍 calculateChecklistProgress Debug:', {
    checklist,
    allSteps,
    totalItems,
    completedItems,
    percentage: Math.round((completedItems / totalItems) * 100)
  });
  
  return Math.round((completedItems / totalItems) * 100);
};

// Helper function to get current step based on checklist
export const getCurrentStep = (checklist) => {
  if (!checklist) return 1;
  
  const step1Progress = calculateStepProgress(checklist.planTrip);
  const step2Progress = calculateStepProgress(checklist.prepareTrip);
  const step3Progress = calculateStepProgress(checklist.activityTrip);
  
  if (step1Progress < 100) return 1;
  if (step2Progress < 100) return 2;
  if (step3Progress < 100) return 3;
  return 4; // Completed
};

// Helper function to calculate individual step progress
export const calculateStepProgress = (stepChecklist) => {
  if (!stepChecklist) return 0;
  
  const items = Object.values(stepChecklist);
  const completed = items.filter(item => item).length;
  return Math.round((completed / items.length) * 100);
};

// Helper function to update checklist
export const updateChecklist = (checklist, step, item, value) => {
  return {
    ...checklist,
    [step]: {
      ...checklist[step],
      [item]: value
    }
  };
};

